import React, { useState, useEffect } from "react"
import { IoSearchOutline } from "react-icons/io5"

const SearchBar = ({
	searchableItems, // Array of objects with search configurations
	placeholder = "Search...",
	onResultClick,
	width = "w-1/3"
}) => {
	const [searchTerm, setSearchTerm] = useState("")
	const [searchResults, setSearchResults] = useState([])
	const [highlightedIndex, setHighlightedIndex] = useState(-1)
	const [isSearchFocused, setIsSearchFocused] = useState(false)

	// Handle search input change
	const handleSearch = e => {
		const term = e.target.value.toLowerCase()
		setSearchTerm(term)

		if (!term.trim()) {
			setSearchResults([])
			return
		}

		// Collect results from all searchable item types
		const allResults = []

		searchableItems.forEach(config => {
			const {
				items,
				type,
				getSearchableText,
				getId,
				getName,
				getDisplayText,
				iconBgColor,
				iconTextColor
			} = config

			const matchingItems = items
				.filter(item => {
					const searchableText = getSearchableText(item).toLowerCase()
					return searchableText.includes(term)
				})
				.map(item => ({
					type,
					id: getId(item),
					name: getName(item),
					displayText: getDisplayText(item),
					iconBgColor: iconBgColor || "bg-blue-100",
					iconTextColor: iconTextColor || "text-blue-600",
					originalItem: item // Store the original item for reference
				}))

			allResults.push(...matchingItems)
		})

		setSearchResults(allResults)
	}

	// Handle keyboard navigation
	const handleSearchKeyDown = e => {
		if (searchResults.length === 0) return

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault()
				setHighlightedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev))
				break
			case "ArrowUp":
				e.preventDefault()
				setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0))
				break
			case "Enter":
				e.preventDefault()
				if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
					handleSearchResultClick(searchResults[highlightedIndex])
				}
				break
			case "Escape":
				e.preventDefault()
				setSearchTerm("")
				setSearchResults([])
				setHighlightedIndex(-1)
				break
			default:
				break
		}
	}

	// Handle clicking on a search result
	const handleSearchResultClick = result => {
		onResultClick(result)
		setSearchTerm("")
		setSearchResults([])
	}

	// Reset highlighted index when search results change
	useEffect(() => {
		setHighlightedIndex(searchResults.length > 0 ? 0 : -1)
	}, [searchResults.length])

	return (
		<div className={`relative ${width}`}>
			<div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
				<IoSearchOutline className="text-gray-400" />
			</div>
			<input
				type="text"
				className="
				sensitive ph-no-capture
				py-5 px-4 w-full border border-transparent rounded-md pl-8 text-sm h-8 bg-gray-100 text-gray-600 placeholder-gray-500 outline-none focus:outline-none focus:ring-0 focus:border-transparent"
				placeholder={placeholder}
				value={searchTerm}
				onChange={handleSearch}
				onKeyDown={handleSearchKeyDown}
				onFocus={() => setIsSearchFocused(true)}
				onBlur={() => {
					setTimeout(() => setIsSearchFocused(false), 200)
				}}
			/>

			{/* Search results dropdown */}
			{searchResults.length > 0 && isSearchFocused && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
					{searchResults.map((result, index) => (
						<div
							key={`${result.type}-${result.id}`}
							className={`px-4 py-2 cursor-pointer text-left text-sm border-b border-gray-100 last:border-b-0 flex items-center ${
								index === highlightedIndex ? "bg-gray-100" : "hover:bg-gray-50"
							}`}
							onClick={() => handleSearchResultClick(result)}
							onMouseEnter={() => setHighlightedIndex(index)}
						>
							<div
								className={`w-6 h-6 rounded-full ${result.iconBgColor} ${result.iconTextColor} flex items-center justify-center mr-3 flex-shrink-0`}
							>
								<span className="sensitive ph-no-capture text-xs font-medium">
									{result.name
										.split(" ")
										.map(part => part[0])
										.join("")}
								</span>
							</div>
							<div className="flex flex-col">
								<span className="sensitive font-medium">{result.name}</span>
								<span className="sensitive text-xs text-gray-500">
									{result.displayText}
								</span>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default SearchBar
